import axiosInstance, {
  fetcher,
  configMultiPartFormData,
  noRevalidateOnlyStaleOptions,
  revalidateOnlyStaleOptions,
} from 'src/utils/axios';
import { ENDPOINTS } from './endpoints';
import { downloadExcelFile } from './excel_api';
import { useMemo } from 'react';
import useSWR, { mutate } from 'swr';
import { getURL } from 'src/utils/url';

var itemHistoryURL = null;

export function useGetInbounds(params) {
  const URL = getURL(ENDPOINTS.inbound.get_list, params);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchCount: data?.result?.count || {},
      searchResults: data?.result?.list || [],
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetInbound(poId) {
  const URL = ENDPOINTS.inbound.get(poId);
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchMaster: data?.result?.purchaseOrder || {},
      searchResults: data?.result?.list || [],
      searchFeatureSettings: data?.result?.featureSettings || null,
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export function useGetInboundSortList() {
  const URL = ENDPOINTS.inbound.sort_list;

  const { data, isLoading, error, isValidating } = useSWR(
    URL,
    fetcher,
    noRevalidateOnlyStaleOptions,
  );

  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );

  return memoizedValue;
}

export function useGetInboundItemHistory(poId, purchaseOrderLine) {
  const URL = ENDPOINTS.inbound.get_item_history(poId, purchaseOrderLine);
  itemHistoryURL = URL;
  const { data, isLoading, error, isValidating } = useSWR(URL, fetcher, revalidateOnlyStaleOptions);
  const memoizedValue = useMemo(
    () => ({
      searchResults: data?.result || {},
      searchLoading: isLoading,
      searchError: error,
      searchValidating: isValidating,
    }),
    [data?.result, error, isLoading, isValidating],
  );
  return memoizedValue;
}

export async function postInbound(data) {
  var ret = await axiosInstance.post(ENDPOINTS.inbound.post, data);
  await mutate(ENDPOINTS.inbound.get(data.purchaseOrderId));
  return ret.data;
}

export async function deleteInboundReceived(receivingId, cancelReasonCode, poId) {
  var ret = await axiosInstance.delete(
    ENDPOINTS.inbound.delete_received(receivingId, cancelReasonCode),
  );
  await mutate(itemHistoryURL);
  if (poId) await mutate(ENDPOINTS.inbound.get(poId));
  await mutate((key) => typeof key === 'string' && key.startsWith(ENDPOINTS.inbound.get_list));
  return ret.data;
}

export async function getInboundExcelSample() {
  return downloadExcelFile(ENDPOINTS.inbound.get_excel_sample);
}

export async function importInboundExcel({ purchaseOrderId, locationId, excelFile, validateOnly }) {
  const formData = new FormData();
  formData.append('excelFile', excelFile);
  formData.append('purchaseOrderId', purchaseOrderId);
  formData.append('locationId', locationId);
  formData.append('validateOnly', validateOnly);

  var ret = await axiosInstance.post(
    ENDPOINTS.inbound.post_excel_import,
    formData,
    configMultiPartFormData,
  );
  if (!validateOnly && ret.data?.isSuccess) {
    mutate(ENDPOINTS.inbound.get(purchaseOrderId), (d) => d, true);
    await mutate(
      (key) => typeof key === 'string' && key.startsWith(ENDPOINTS.inbound.get_list),
      undefined,
      { revalidate: true },
    );
  }
  return ret.data;
}

export async function getInboundWithoutPoExcelSample() {
  return downloadExcelFile(ENDPOINTS.inbound.get_excel_sample_without_po);
}

export async function importInboundWithoutPoExcel({
  userId,
  locationId,
  productCodeType,
  excelFile,
  validateOnly,
}) {
  const formData = new FormData();
  formData.append('excelFile', excelFile);
  formData.append('userId', userId);
  formData.append('locationId', locationId);
  formData.append('productCodeType', productCodeType);
  formData.append('validateOnly', validateOnly);

  var ret = await axiosInstance.post(
    ENDPOINTS.inbound.post_excel_import_without_po,
    formData,
    configMultiPartFormData,
  );
  if (!validateOnly && ret.data?.isSuccess) {
    await mutate(
      (key) => typeof key === 'string' && key.startsWith(ENDPOINTS.inbound.get_list),
      undefined,
      { revalidate: true },
    );
  }
  return ret.data;
}

export async function getInboundExcelList(params) {
  const URL = getURL(ENDPOINTS.inbound.get_excel_list, params);
  const res = await axiosInstance.get(URL);
  return res.data?.result?.list || [];
}
